import React from 'react';
import { Globe2, Lock, MapPin, CalendarDays } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Localized } from '../../types/notice';
import type { CityDistrict } from '../../utils/districts';
import { DATA_AS_OF } from '../../data/requestDashboard';
import { DateRangePicker, type DateRange } from './DateRangePicker';
interface DashboardScopeProps {
  name: string;
  role: string;
  city: string;
  district: CityDistrict | null;
  dateRange: DateRange;
  onDateRangeChange: (r: DateRange) => void;
  periodLabel: Localized;
}

/** Territory and period the dashboard covers, always visible at the top. */
export function DashboardScope({
  name,
  role,
  city,
  district,
  dateRange,
  onDateRangeChange,
  periodLabel
}: DashboardScopeProps) {
  const {
    t,
    lang
  } = useLanguage();
  const letters = district ? district.sectors.map(s => s.label[lang].replace(/^(Secteurs?|Sectors?) /, '')) : [];
  const list = letters.length > 1 ? `${letters.slice(0, -1).join(', ')} ${t('et', 'and')} ${letters[letters.length - 1]}` : letters[0];
  return <div role="status" className={`mt-3 rounded-xl border px-4 py-3 flex flex-col md:flex-row md:items-center gap-3 md:gap-6 font-body ${district ? 'border-indigo-200 bg-indigo-50' : 'border-slate-200 bg-slate-50'}`} data-id="element-5292" data-mp="36zxy5m3os89k">
      <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-5293" data-mp="3gt6eox3sgg1i">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${district ? 'bg-[#4F46E5] text-white' : 'bg-white border border-slate-200 text-slate-500'}`} data-id="element-5294" data-mp="2t212crx245vy">
          {district ? <Lock className="w-4 h-4" data-id="element-5295" data-mp="31zjqi55zz0c2" /> : <Globe2 className="w-4 h-4" data-id="element-5296" data-mp="2x2bg4qr3c0ld" />}
        </div>
        <div className="min-w-0" data-id="element-5297" data-mp="2x4xmin28wa20">
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1" data-id="element-5298" data-mp="hbf8jof2z9ke">
            <MapPin className="w-3 h-3" data-id="element-5299" data-mp="cddlytig4s46" /> {t('Territoire', 'Territory')}
          </div>
          <div className="text-base font-bold text-[#0F172A] font-heading truncate" data-id="element-5300" data-mp="3653krlzhwxf0">
            {district ? `${district.label[lang]} · ${t('secteur', 'sector')}${letters.length > 1 ? 's' : ''} ${list}` : t(`Toute la ville · ${city}`, `Whole city · ${city}`)}
          </div>
          <div className="text-xs text-slate-500 truncate" data-id="element-5301" data-mp="3k4le8enqkyup">
            {name}, {role}
            {district && ` · ${t('vue limitée à votre district', 'view limited to your district')}`}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 md:border-l md:border-slate-200 md:pl-6" data-id="element-5302" data-mp="2rg7e9td37sgi">
        <div data-id="element-5303" data-mp="2wdz50k1zx8j4">
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1" data-id="element-5304" data-mp="2wd7nue95bdcd">
            <CalendarDays className="w-3 h-3" data-id="element-5305" data-mp="317k7opejaqon" /> {t('Période', 'Period')}
          </div>
          <div className="text-base font-bold text-[#0F172A] font-heading" data-id="element-5306" data-mp="23ptl1zirnnkc">{periodLabel[lang]}</div>
          <div className="text-xs text-slate-500" data-id="element-5307" data-mp="28ndbrywnhst6">{DATA_AS_OF[lang]}</div>
        </div>
        <DateRangePicker value={dateRange} onChange={onDateRangeChange} data-id="element-5308" data-mp="2155d29190elb" />
      </div>
    </div>;
}